import { Component, EventEmitter, OnInit } from '@angular/core';
import { IntroducerService } from '../introducer.service';
import { Introducer } from '../introducer';
import { NotificationService } from '../../shared/notification.service';

@Component({
  selector: 'app-introducer-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class IntroducerListComponent implements OnInit {
  public introducer: Introducer;

  /**
   * A list of all the introducers currently loaded.
   *
   * @type {Introducer[]}
   */
  public introducers: Introducer[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of introducers.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of introducers that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of introducers that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a introducer has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public introducerDeleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {IntroducerService} introducerService
   * @param {NotificationService} notificationService
   */
  constructor(
    private introducerService: IntroducerService,
    private notificationService: NotificationService,
  ) {
    this.introducerService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.introducerService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.introducerService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.introducerService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the introducer list.
   */
  public ngOnInit(): void {
    this.loadIntroducers(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * introducer list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadIntroducers(pageNumber);
  }

  /**
   * Loads a list of introducers using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadIntroducers(page?: number, limit?: number): void {
    const listIntroducers = this.introducerService
      .listIntroducers(page, limit)
      .subscribe((introducers: Introducer[]) => {
        this.introducers = introducers;
        listIntroducers.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate introducer ID
   * to delete the introducer.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.introducerService.deleteIntroducer(id).subscribe(
      deleted => {
        this.notificationService.success(
          `Introducer Deleted`,
          `Introducer with ID ${id} was deleted.`,
          5,
        );
        this.introducerDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `Introducer Not Deleted`,
          err.error.message,
          10,
        );
        this.introducerDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
